// ===================================================================
// TABLE MANAGEMENT DATA STRUCTURE AND UTILITIES
// ===================================================================
// This file contains data models, validation functions, and utilities
// for the Table Management System
// ===================================================================

// ===================================================================
// 1. DATA TEMPLATES
// ===================================================================

// Table Template
export const tableTemplate = {
  id: '',
  number: '',
  section: '',
  sectionId: '',
  sectionName: '',
  capacity: 0,
  shape: 'square', // square, round, rectangle
  description: '',
  isActive: true, // Corresponds to available/ready
  isOccupied: false, // New field for advanced status
  position: { x: 0, y: 0 }, // For floor plan
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

// Section Template
export const sectionTemplate = {
  id: '',
  name: '',
  description: '',
  color: '#3B82F6',
  displayOrder: 0,
  isActive: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

// ===================================================================
// 2. DEFAULT/SAMPLE DATA (Updated from user's POS configuration)
// ===================================================================

const SECTION_MAP = {
  'Common Hall': { id: 'section-ch', color: '#10B981', description: 'General Dining Area' },
  'AC': { id: 'section-ac', color: '#3B82F6', description: 'Air Conditioned Seating' },
  'Family Hall': { id: 'section-fh', color: '#F59E0B', description: 'Large Group and Family Tables' },
  'Dastarkhan': { id: 'section-ds', color: '#EF4444', description: 'Traditional Floor Seating' },
  'Lawn': { id: 'section-lw', color: '#06B6D4', description: 'Outdoor/Lawn Seating' },
  'Parcel': { id: 'section-pc', color: '#6366F1', description: 'Takeaway/Counter Terminals' },
};

export const defaultSections = Object.keys(SECTION_MAP).map((name, index) => ({
  id: SECTION_MAP[name].id,
  name: name,
  description: SECTION_MAP[name].description,
  color: SECTION_MAP[name].color,
  displayOrder: index + 1,
  isActive: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
}));

// User's detailed table configuration
const RAW_TABLES = [
  // Common Hall (12)
  { id: 'ch1', name: 'CH-1', number: 1, section: 'Common Hall', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 40, y: 28 }, isReservable: true, isAvailable: true, isOccupied: false, minSpend: 0, features: ['near-entrance'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ch2', name: 'CH-2', number: 2, section: 'Common Hall', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 140, y: 28 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ch3', name: 'CH-3', number: 3, section: 'Common Hall', capacity: 2, type: 'TABLE', shape: 'round', coords: { x: 240, y: 28 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['near-window'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ch4', name: 'CH-4', number: 4, section: 'Common Hall', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 340, y: 28 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['booth'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ch5', name: 'CH-5', number: 5, section: 'Common Hall', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 40, y: 110 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ch6', name: 'CH-6', number: 6, section: 'Common Hall', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 140, y: 110 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['highchair'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ch7', name: 'CH-7', number: 7, section: 'Common Hall', capacity: 2, type: 'TABLE', shape: 'round', coords: { x: 240, y: 110 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ch8', name: 'CH-8', number: 8, section: 'Common Hall', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 340, y: 110 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ch9', name: 'CH-9', number: 9, section: 'Common Hall', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 40, y: 200 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ch10', name: 'CH-10', number: 10, section: 'Common Hall', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 140, y: 200 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ch11', name: 'CH-11', number: 11, section: 'Common Hall', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 240, y: 200 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ch12', name: 'CH-12', number: 12, section: 'Common Hall', capacity: 2, type: 'TABLE', shape: 'round', coords: { x: 340, y: 200 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['near-restroom'], waiterId: null, mergedWith: [], notes: '' },

  // AC (8)
  { id: 'ac1', name: 'AC-1', number: 1, section: 'AC', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 520, y: 28 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['air-conditioned'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ac2', name: 'AC-2', number: 2, section: 'AC', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 620, y: 28 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['near-window'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ac3', name: 'AC-3', number: 3, section: 'AC', capacity: 2, type: 'TABLE', shape: 'round', coords: { x: 720, y: 28 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ac4', name: 'AC-4', number: 4, section: 'AC', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 520, y: 110 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['booth'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ac5', name: 'AC-5', number: 5, section: 'AC', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 620, y: 110 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ac6', name: 'AC-6', number: 6, section: 'AC', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 720, y: 110 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ac7', name: 'AC-7', number: 7, section: 'AC', capacity: 2, type: 'TABLE', shape: 'round', coords: { x: 520, y: 200 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ac8', name: 'AC-8', number: 8, section: 'AC', capacity: 8, type: 'TABLE', shape: 'rectangle', coords: { x: 620, y: 200 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['family'], waiterId: null, mergedWith: [], notes: '' },

  // Family Hall (10) — bigger capacities
  { id: 'fh1', name: 'FH-1', number: 1, section: 'Family Hall', capacity: 8, type: 'TABLE', shape: 'rectangle', coords: { x: 40, y: 360 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['family'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'fh2', name: 'FH-2', number: 2, section: 'Family Hall', capacity: 8, type: 'TABLE', shape: 'rectangle', coords: { x: 160, y: 360 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['partitionable'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'fh3', name: 'FH-3', number: 3, section: 'Family Hall', capacity: 10, type: 'TABLE', shape: 'rectangle', coords: { x: 280, y: 360 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['event-ready'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'fh4', name: 'FH-4', number: 4, section: 'Family Hall', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 400, y: 360 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'fh5', name: 'FH-5', number: 5, section: 'Family Hall', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 520, y: 360 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'fh6', name: 'FH-6', number: 6, section: 'Family Hall', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 640, y: 360 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['near-stage'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'fh7', name: 'FH-7', number: 7, section: 'Family Hall', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 760, y: 360 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'fh8', name: 'FH-8', number: 8, section: 'Family Hall', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 880, y: 360 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'fh9', name: 'FH-9', number: 9, section: 'Family Hall', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 1000, y: 360 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'fh10', name: 'FH-10', number: 10, section: 'Family Hall', capacity: 8, type: 'TABLE', shape: 'rectangle', coords: { x: 1120, y: 360 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['family'], waiterId: null, mergedWith: [], notes: '' },

  // Dastarkhan (8) — traditional seating (floor/dastarkhan style)
  { id: 'ds1', name: 'DK-1', number: 1, section: 'Dastarkhan', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 40, y: 520 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['floor-seating'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ds2', name: 'DK-2', number: 2, section: 'Dastarkhan', capacity: 8, type: 'TABLE', shape: 'rectangle', coords: { x: 180, y: 520 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['floor-seating'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ds3', name: 'DK-3', number: 3, section: 'Dastarkhan', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 320, y: 520 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ds4', name: 'DK-4', number: 4, section: 'Dastarkhan', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 460, y: 520 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['low-table'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ds5', name: 'DK-5', number: 5, section: 'Dastarkhan', capacity: 10, type: 'TABLE', shape: 'rectangle', coords: { x: 600, y: 520 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['event-ready'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ds6', name: 'DK-6', number: 6, section: 'Dastarkhan', capacity: 8, type: 'TABLE', shape: 'rectangle', coords: { x: 740, y: 520 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ds7', name: 'DK-7', number: 7, section: 'Dastarkhan', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 880, y: 520 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'ds8', name: 'DK-8', number: 8, section: 'Dastarkhan', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 1020, y: 520 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },

  // Lawn (5) — outdoors
  { id: 'lw1', name: 'LW-1', number: 1, section: 'Lawn', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 40, y: 680 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['outdoor'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'lw2', name: 'LW-2', number: 2, section: 'Lawn', capacity: 8, type: 'TABLE', shape: 'rectangle', coords: { x: 220, y: 680 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['shade'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'lw3', name: 'LW-3', number: 3, section: 'Lawn', capacity: 4, type: 'TABLE', shape: 'rectangle', coords: { x: 400, y: 680 }, isReservable: true, isAvailable: true, isOccupied: false, features: [], waiterId: null, mergedWith: [], notes: '' },
  { id: 'lw4', name: 'LW-4', number: 4, section: 'Lawn', capacity: 6, type: 'TABLE', shape: 'rectangle', coords: { x: 580, y: 680 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['near-stage'], waiterId: null, mergedWith: [], notes: '' },
  { id: 'lw5', name: 'LW-5', number: 5, section: 'Lawn', capacity: 8, type: 'TABLE', shape: 'rectangle', coords: { x: 760, y: 680 }, isReservable: true, isAvailable: true, isOccupied: false, features: ['party-ready'], waiterId: null, mergedWith: [], notes: '' },

  // Parcel / Takeaway terminals (6)
  { id: 'parcel-1', name: 'PL-1', number: 1, section: 'Parcel', capacity: 0, type: 'PARCEL', shape: 'rect', coords: { x: 980, y: 700 }, isReservable: false, isAvailable: true, isOccupied: false, features: ['counter'], waiterId: null, mergedWith: [], notes: 'Takeaway counter #1' },
  { id: 'parcel-2', name: 'PL-2', number: 2, section: 'Parcel', capacity: 0, type: 'PARCEL', shape: 'rect', coords: { x: 1020, y: 700 }, isReservable: false, isAvailable: true, isOccupied: false, features: ['counter'], waiterId: null, mergedWith: [], notes: 'Takeaway counter #2' },
  { id: 'parcel-3', name: 'PL-3', number: 3, section: 'Parcel', capacity: 0, type: 'PARCEL', shape: 'rect', coords: { x: 1060, y: 700 }, isReservable: false, isAvailable: true, isOccupied: false, features: ['counter'], waiterId: null, mergedWith: [], notes: 'Takeaway counter #3' },
  { id: 'parcel-4', name: 'PL-4', number: 4, section: 'Parcel', capacity: 0, type: 'PARCEL', shape: 'rect', coords: { x: 1100, y: 700 }, isReservable: false, isAvailable: true, isOccupied: false, features: ['counter'], waiterId: null, mergedWith: [], notes: 'Takeaway counter #4' },
  { id: 'parcel-5', name: 'PL-5', number: 5, section: 'Parcel', capacity: 0, type: 'PARCEL', shape: 'rect', coords: { x: 1140, y: 700 }, isReservable: false, isAvailable: true, isOccupied: false, features: ['counter'], waiterId: null, mergedWith: [], notes: 'Takeaway counter #5' },
  { id: 'parcel-6', name: 'PL-6', number: 6, section: 'Parcel', capacity: 0, type: 'PARCEL', shape: 'rect', coords: { x: 1180, y: 700 }, isReservable: false, isAvailable: true, isOccupied: false, features: ['counter'], waiterId: null, mergedWith: [], notes: 'Takeaway counter #6' },
];


export const sampleTables = RAW_TABLES.map(table => {
  const sectionInfo = SECTION_MAP[table.section];

  return {
    id: table.id,
    number: table.name, // e.g., 'CH-1', 'DST-5'
    section: table.section,
    sectionId: sectionInfo ? sectionInfo.id : '',
    sectionName: table.section,
    capacity: table.capacity,
    // Normalize shapes for the UI: square, round, rectangle
    shape: table.shape === 'round' ? 'round' : (table.shape === 'rectangle' ? 'rectangle' : 'square'),
    description: table.notes || table.features?.join(', ') || '',
    isActive: table.isAvailable, // We map isAvailable to isActive for the current app logic
    isOccupied: table.isOccupied, // Keep the original status for future use
    position: table.coords ? { x: table.coords.x, y: table.coords.y } : { x: 0, y: 0 },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
});


// ===================================================================
// 3. VALIDATION FUNCTIONS
// ... (rest of the file remains the same)
// ===================================================================

// Validate table
export const validateTable = (table) => {
  const errors = {};

  if (!table.number || table.number.trim() === '') {
    errors.number = 'Table number is required';
  }

  if (!table.section || table.section.trim() === '') {
    errors.section = 'Section is required';
  }

  if (table.capacity === undefined || table.capacity < 0) {
    errors.capacity = 'Valid capacity is required';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Validate section
export const validateSection = (section) => {
  const errors = {};

  if (!section.name || section.name.trim() === '') {
    errors.name = 'Section name is required';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// ===================================================================
// 4. UTILITY FUNCTIONS
// ===================================================================

// Calculate table statistics
export const calculateTableStats = (tables) => {
  const total = tables.length;
  const active = tables.filter(t => t.isActive).length;
  const inactive = total - active;
  const totalCapacity = tables.reduce((sum, t) => sum + (t.capacity || 0), 0);
  const avgCapacity = total > 0 ? Math.round(totalCapacity / total) : 0;

  return {
    total,
    active,
    inactive,
    totalCapacity,
    avgCapacity
  };
};

// Get section statistics
export const getSectionStats = (tables, sections) => {
  return sections.map(section => {
    const sectionTables = tables.filter(t => t.sectionId === section.id);
    const capacity = sectionTables.reduce((sum, t) => sum + (t.capacity || 0), 0);

    return {
      ...section,
      tableCount: sectionTables.length,
      totalCapacity: capacity,
      activeTables: sectionTables.filter(t => t.isActive).length
    };
  });
};

// Generate unique ID
export const generateId = (prefix = 'table') => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Clone table
export const cloneTable = (table) => {
  return {
    ...table,
    id: generateId('table'),
    number: `${table.number}-copy`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

// ===================================================================
// 5. FILTERING & SEARCHING FUNCTIONS
// ===================================================================

// Search tables
export const searchTables = (tables, query) => {
  if (!query || query.trim() === '') return tables;

  const searchTerm = query.toLowerCase().trim();
  return tables.filter(table =>
    table.number.toLowerCase().includes(searchTerm) ||
    table.sectionName?.toLowerCase().includes(searchTerm) ||
    table.description?.toLowerCase().includes(searchTerm)
  );
};

// Filter by section
export const filterBySection = (tables, sectionId) => {
  if (sectionId === 'all') return tables;
  return tables.filter(table => table.sectionId === sectionId);
};

// Filter by status
export const filterByStatus = (tables, status) => {
  if (status === 'all') return tables;
  const isActive = status === 'active';
  return tables.filter(table => table.isActive === isActive);
};

// Sort tables
export const sortTables = (tables, sortBy) => {
  const sorted = [...tables];

  switch (sortBy) {
    case 'number-asc':
      return sorted.sort((a, b) => {
        const numA = parseInt(a.number.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.number.replace(/\D/g, '')) || 0;
        if (numA !== numB) return numA - numB;
        return a.number.localeCompare(b.number);
      });
    case 'number-desc':
      return sorted.sort((a, b) => {
        const numA = parseInt(a.number.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.number.replace(/\D/g, '')) || 0;
        if (numA !== numB) return numB - numA;
        return b.number.localeCompare(a.number);
      });
    case 'capacity-asc':
      return sorted.sort((a, b) => a.capacity - b.capacity);
    case 'capacity-desc':
      return sorted.sort((a, b) => b.capacity - a.capacity);
    case 'section':
      return sorted.sort((a, b) => a.sectionName.localeCompare(b.sectionName));
    default:
      return sorted;
  }
};

// ===================================================================
// 6. DATA TRANSFORMATION FUNCTIONS
// ===================================================================

// Prepare table for API
export const prepareTableForAPI = (table) => {
  const prepared = { ...table };

  // Ensure timestamps
  if (!prepared.createdAt) {
    prepared.createdAt = new Date().toISOString();
  }
  prepared.updatedAt = new Date().toISOString();

  return prepared;
};

// Parse table from API
export const parseTableFromAPI = (table) => {
  return {
    ...table,
    createdAt: table.createdAt || new Date().toISOString(),
    updatedAt: table.updatedAt || new Date().toISOString(),
    isActive: table.isActive !== undefined ? table.isActive : true,
    capacity: parseInt(table.capacity) || 0,
    position: table.position || { x: 0, y: 0 }
  };
};

// ===================================================================
// 7. BULK OPERATIONS HELPERS
// ===================================================================

// Bulk update tables
export const bulkUpdateTables = (tables, updates) => {
  return tables.map(table => ({
    ...table,
    ...updates,
    updatedAt: new Date().toISOString()
  }));
};

// Bulk delete tables
export const bulkDeleteTables = (allTables, idsToDelete) => {
  return allTables.filter(table => !idsToDelete.includes(table.id));
};

// Bulk toggle status
export const bulkToggleStatus = (tables, isActive) => {
  return bulkUpdateTables(tables, { isActive });
};

// ===================================================================
// 8. LOCAL STORAGE HELPERS
// ===================================================================

const STORAGE_KEYS = {
  TABLES: 'restaurant_tables',
  SECTIONS: 'restaurant_sections',
  VIEW_PREFERENCE: 'table_view_preference'
};

// Save to local storage
export const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    return false;
  }
};

// Load from local storage
export const loadFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return defaultValue;
  }
};

// Save tables
export const saveTables = (tables) => {
  return saveToLocalStorage(STORAGE_KEYS.TABLES, tables);
};

// Load tables
export const loadTables = () => {
  return loadFromLocalStorage(STORAGE_KEYS.TABLES, sampleTables);
};

// Save sections
export const saveSections = (sections) => {
  return saveToLocalStorage(STORAGE_KEYS.SECTIONS, sections);
};

// Load sections
export const loadSections = () => {
  return loadFromLocalStorage(STORAGE_KEYS.SECTIONS, defaultSections);
};

// Save view preference
export const saveViewPreference = (view) => {
  return saveToLocalStorage(STORAGE_KEYS.VIEW_PREFERENCE, view);
};

// Load view preference
export const loadViewPreference = () => {
  return loadFromLocalStorage(STORAGE_KEYS.VIEW_PREFERENCE, 'grid');
};

// ===================================================================
// EXPORTS
// ===================================================================

export default {
  // Templates
  tableTemplate,
  sectionTemplate,

  // Sample Data
  defaultSections,
  sampleTables,

  // Validation
  validateTable,
  validateSection,

  // Utilities
  calculateTableStats,
  getSectionStats,
  generateId,
  cloneTable,

  // Filtering & Searching
  searchTables,
  filterBySection,
  filterByStatus,
  sortTables,

  // Data Transformation
  prepareTableForAPI,
  parseTableFromAPI,

  // Bulk Operations
  bulkUpdateTables,
  bulkDeleteTables,
  bulkToggleStatus,

  // Local Storage
  saveTables,
  loadTables,
  saveSections,
  loadSections,
  saveViewPreference,
  loadViewPreference
};